import { useEffect, useState, useMemo } from "react";
import {
  Loader2,
  Image as ImageIcon,
  X,
  ChevronLeft,
  ChevronRight,
  Search,
} from "lucide-react";
import {
  galleryAPI,
  galleryCategoriesAPI,
  type GalleryItem,
  type GalleryCategory,
} from "../../services/api";
import eventsHeroBg from "../../assets/media-background.jpg"; // Re-using background until custom is provided
import { SitePagination } from "../ui/SitePagination";

const ITEMS_PER_PAGE = 8;

export default function MediaGalleryPage() {
  const [categories, setCategories] = useState<GalleryCategory[]>([]);
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<number | string>("All");
  const [selectedItem, setSelectedItem] = useState<GalleryItem | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const cats = await galleryCategoriesAPI.getCategoryList();
        setCategories(cats);
      } catch (error) {
        console.error("Failed to fetch gallery categories:", error);
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchGalleryItems = async () => {
      setLoading(true);
      try {
        const params: Record<string, any> = {};
        if (activeCategory !== "All") {
          params.category = activeCategory;
        }
        if (debouncedSearch) {
          params.search = debouncedSearch;
        }

        const items = await galleryAPI.getGalleryList(params);
        if (cancelled) {
          return;
        }
        setGalleryItems(
          items.sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
        );
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch gallery items:", error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    fetchGalleryItems();
    return () => {
      cancelled = true;
    };
  }, [activeCategory, debouncedSearch]);

  const totalPages = Math.max(
    1,
    Math.ceil(galleryItems.length / ITEMS_PER_PAGE)
  );

  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return galleryItems.slice(start, start + ITEMS_PER_PAGE);
  }, [galleryItems, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, debouncedSearch]);

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(galleryItems.length / ITEMS_PER_PAGE));
    setCurrentPage(prev => (prev > tp ? tp : prev));
  }, [galleryItems.length]);

  const handlePrev = () => {
    if (!selectedItem) {
      return;
    }
    const currentIndex = galleryItems.findIndex(
      (i: GalleryItem) => i.id === selectedItem.id
    );
    const prevIndex =
      (currentIndex - 1 + galleryItems.length) % galleryItems.length;
    setSelectedItem(galleryItems[prevIndex]);
  };

  const handleNext = () => {
    if (!selectedItem) {
      return;
    }
    const currentIndex = galleryItems.findIndex(
      (i: GalleryItem) => i.id === selectedItem.id
    );
    const nextIndex = (currentIndex + 1) % galleryItems.length;
    setSelectedItem(galleryItems[nextIndex]);
  };

  return (
    <div className="min-h-screen bg-[#FFF9F0]">
      {/* Hero Section */}
      <section className="relative flex min-h-[40vh] items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={eventsHeroBg}
            alt="Gallery hero"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#7A1E1E]/70 to-[#8B2E2E]/60" />
        </div>

        <div className="relative z-10 mx-auto max-w-[1400px] px-4 text-white sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="mb-4 text-center text-4xl font-bold lg:text-5xl">
              Media Gallery
            </h1>
            <p className="mt-4 text-lg leading-relaxed text-gray-100">
              Explore our collection of memorable moments, events, and
              milestones within the JBF community. Glimpse into our shared
              experiences through these vibrant highlights.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="sticky top-[8.9rem] z-20 border-b border-gray-100 bg-white/80 shadow-sm backdrop-blur-md">
        <div className="mx-auto max-w-[1400px] px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            {/* Search Bar */}
            <div className="relative w-full max-w-md">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search gallery..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full rounded-2xl border border-gray-100 bg-gray-50/50 py-2 pl-11 pr-4 text-sm outline-none transition-all focus:border-[#7A1E1E]/30 focus:bg-white focus:ring-4 focus:ring-[#7A1E1E]/5"
              />
            </div>

            {/* Category Filters */}
            <div className="scrollbar-hide flex items-center gap-3 overflow-x-auto pb-2 md:pb-0">
              <button
                onClick={() => setActiveCategory("All")}
                className={`shrink-0 rounded-full border border-transparent px-5 py-1.5 text-sm transition-all ${activeCategory === "All"
                    ? "bg-[#7A1E1E] text-white shadow-md shadow-[#7A1E1E]/20"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
              >
                All
              </button>
              {categories.map((cat: GalleryCategory) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`shrink-0 rounded-full border border-transparent px-5 py-1.5 text-sm transition-all ${String(activeCategory) === String(cat.id)
                      ? "bg-[#7A1E1E] text-white shadow-md shadow-[#7A1E1E]/20"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="bg-gradient-to-b from-white to-[#FFF5E6] py-12 sm:py-16">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex h-64 flex-col items-center justify-center gap-4">
              <Loader2 className="h-10 w-10 animate-spin text-[#D4A017]" />
              <p className="font-medium text-gray-500">
                Loading gallery images...
              </p>
            </div>
          ) : galleryItems.length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed border-gray-200 bg-white">
              <ImageIcon className="h-12 w-12 text-gray-300" />
              <p className="text-xl font-medium text-gray-500">
                No media found in this category.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {paginatedItems.map((item: GalleryItem) => (
                  <div
                    key={item.id}
                    onClick={() => setSelectedItem(item)}
                    className="group relative cursor-pointer overflow-hidden rounded-[24px] border-2 border-transparent bg-white transition-all duration-300 hover:border-[#F28C28] hover:shadow-xl hover:shadow-orange-500/10"
                  >
                    <div className="relative aspect-square w-full overflow-hidden bg-gray-100">
                      {/* Image */}
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.title}
                          className="h-full w-full object-cover transition-transform duration-700 ease-in-out group-hover:scale-110"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-gray-300">
                          <ImageIcon className="h-16 w-16 opacity-30" />
                        </div>
                      )}
                      {/* Gradient & Hover Content (Slides in from bottom) */}
                      <div className="absolute inset-x-0 bottom-0 z-10 flex translate-y-full flex-col justify-end bg-gradient-to-t from-black/90 via-black/60 to-transparent p-5 pt-12 text-white transition-transform duration-300 ease-in-out group-hover:translate-y-0">
                        <h3 className="mb-1 text-base font-bold leading-tight sm:text-lg">
                          {item.title}
                        </h3>
                        {item.description && (
                          <p className="mt-1 line-clamp-3 text-xs font-medium leading-snug text-gray-300 sm:text-sm">
                            {item.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-10">
                <SitePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={galleryItems.length}
                  pageSize={ITEMS_PER_PAGE}
                  onPageChange={setCurrentPage}
                  itemLabel="items"
                />
              </div>
            </>
          )}
        </div>
      </section>

      {/* Lightbox / Detail View Overlay */}
      {selectedItem && (
        <div className="fixed inset-0 z-[200] flex flex-col items-center justify-center gap-4 overflow-y-auto bg-black/95 py-20 transition-all duration-300 sm:gap-8">
          {/* Header Controls */}
          <div className="absolute left-0 right-0 top-0 z-[210] flex items-center justify-between p-6 sm:p-10">
            <div className="flex flex-col">
              <h2 className="text-xl font-black uppercase leading-none tracking-tighter text-[#D4A017] sm:text-3xl">
                Media <span className="text-white opacity-80">Gallery</span>
              </h2>
              <p className="mt-1 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest text-white/40 sm:text-xs">
                <span className="h-0.5 w-4 bg-[#D4A017]" /> Relive the moments
                of glory
              </p>
            </div>
            <button
              onClick={() => setSelectedItem(null)}
              className="group flex h-12 w-12 items-center justify-center rounded-full border border-white/5 bg-white/10 text-white shadow-xl transition-all hover:scale-110 hover:bg-white/20 active:scale-95"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Main Focus Area */}
          <div className="relative flex w-full max-w-6xl flex-col items-center px-4 sm:px-12">
            {/* Navigation Arrows */}
            <button
              onClick={handlePrev}
              className="absolute left-0 top-1/2 z-50 -translate-y-1/2 rounded-full bg-black/40 p-3 text-white/50 backdrop-blur-md transition-all hover:scale-110 hover:bg-[#D4A017] hover:text-white sm:left-4 sm:p-4"
            >
              <ChevronLeft className="h-6 w-6 sm:h-8 sm:w-8" />
            </button>
            <button
              onClick={handleNext}
              className="absolute right-0 top-1/2 z-50 -translate-y-1/2 rounded-full bg-black/40 p-3 text-white/50 backdrop-blur-md transition-all hover:scale-110 hover:bg-[#D4A017] hover:text-white sm:right-4 sm:p-4"
            >
              <ChevronRight className="h-6 w-6 sm:h-8 sm:w-8" />
            </button>

            {/* Centered Image Card */}
            <div className="group-detail relative aspect-[4/3] w-full max-w-[600px] overflow-hidden rounded-[32px] border border-white/10 shadow-[0_0_100px_rgba(212,160,23,0.15)]">
              <img
                src={selectedItem.image}
                alt={selectedItem.title}
                className="animate-in fade-in zoom-in-95 h-full w-full object-cover duration-500"
              />
            </div>

            {/* Item Details */}
            <div className="animate-in slide-in-from-bottom-4 mt-4 text-center duration-500">
              <h3 className="mb-2 text-2xl font-black uppercase tracking-tight text-white underline decoration-[#D4A017] selection:line-through sm:text-4xl">
                {selectedItem.title}
              </h3>
              <p className="mb-4 max-w-2xl text-sm font-medium italic text-white/70 sm:text-base">
                &quot;{selectedItem.description}&quot;
              </p>
              <div className="flex items-center justify-center gap-4">
                <span className="text-lg font-bold text-[#D4A017] sm:text-xl">
                  {selectedItem.year}
                </span>
                <span className="h-1 w-1 rounded-full bg-white/20" />
                <span className="text-xs font-black uppercase tracking-widest text-white/40 sm:text-sm">
                  Category:{" "}
                  <span className="text-white/80 underline decoration-[#D4A017]/40 underline-offset-4">
                    {selectedItem.category_name ??
                      categories.find(
                        (c: GalleryCategory) =>
                          String(c.id) === String(selectedItem.category)
                      )?.name ??
                      "JBF Event"}
                  </span>
                </span>
              </div>
            </div>
          </div>

          {/* Bottom Thumbnail Grid (Preview) */}
          <div className="w-full overflow-hidden border-t border-white/5 bg-white/5 py-6 backdrop-blur-sm">
            <div className="mx-auto max-w-[1400px] px-6">
              <div className="scrollbar-hide flex items-center gap-4 overflow-x-auto pb-2">
                {galleryItems.map((item: GalleryItem) => (
                  <button
                    key={item.id}
                    onClick={() => setSelectedItem(item)}
                    className={`relative h-20 w-28 shrink-0 overflow-hidden rounded-xl border-2 transition-all duration-300 sm:h-24 sm:w-32 ${String(selectedItem.id) === String(item.id)
                        ? "scale-105 border-[#D4A017] shadow-[0_0_20px_rgba(212,160,23,0.4)]"
                        : "border-transparent opacity-40 hover:opacity-100"
                      }`}
                  >
                    <img
                      src={item.image}
                      className="h-full w-full object-cover"
                      alt=""
                    />
                    {String(selectedItem.id) === String(item.id) && (
                      <div className="absolute inset-0 bg-[#D4A017]/10" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
